import { toNestErrors, validateFieldsNatively } from '@hookform/resolvers';
import { createValidatorInstance } from '@lotta/shared/util-schema-validation';
import type { Schema, Options, DefinedError } from 'ajv';
import ajvErrors from 'ajv-errors';
import type { FieldValues, Resolver, ResolverOptions, ResolverResult } from 'react-hook-form';
import { parseErrorSchema } from './utils/error-parser';

/**
 * Custom Ajv resolver for react-hhok-form. The resolver has been created to support additioal JSON formats.
 * @param schema The JSON schema of the data to be validated
 * @param schemaOptions Ajv options
 * @returns A Ajv resolver which can be used with react-hook-form
 */
export const createValidationResolver = <TFieldValues extends FieldValues>(
  schema: Schema,
  schemaOptions?: Options
): Resolver<TFieldValues> => {
  const ajv = ajvErrors(
    createValidatorInstance({
      allErrors: true,
      validateSchema: true,
      coerceTypes: true,
      ...schemaOptions,
    })
  );
  const validate = ajv.compile(schema);

  return async <TContext>(
    values: TFieldValues,
    context: TContext,
    options: ResolverOptions<TFieldValues>
  ): Promise<ResolverResult<TFieldValues>> => {
    const valid = validate(values);

    if (!valid) {
      return {
        values: {},
        errors: toNestErrors(
          parseErrorSchema(
            validate.errors as DefinedError[],
            !options.shouldUseNativeValidation && options.criteriaMode === 'all'
          ),
          options
        ),
      };
    }

    options.shouldUseNativeValidation && validateFieldsNatively({}, options);

    return {
      values,
      errors: {},
    };
  };
};
